/**
 * Timeline Lazy Loading - Mobile-Friendly Version
 * Fixed scroll thresholds and responsive image handling
 * Version: 2.2 - Mobile Optimized
 */

(function () {
  'use strict';

  var config = {
    imageSelector:
      '.timeline-entry img, [class*="holder"] img, [class*="lineage"] img',
    timelineSelector: '.timeline-entry, [class*="holder"], [class*="lineage"]',
    initialVisible: 5,
    batchSize: 3,
    rootMargin: '200px 0px',
    threshold: 0.1,
    fadeInDuration: 300,
    scrollThreshold: 0.6, // Load when 60% scrolled
    debounceDelay: 150,
  };

  var imageObserver = null;
  var loadedImages = new Set();
  var isLoading = false;
  var scrollTimeout = null;
  var totalEntries = 0;
  var currentlyVisible = 0;
  var originalStyles = new Map(); // Store original image styles

  function supportsIntersectionObserver() {
    return 'IntersectionObserver' in window;
  }

  function isMobileDevice() {
    return window.innerWidth <= 768 ||
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
  }

  function initLazyLoading() {
    console.log('Timeline Lazy Loading v2.2 - Mobile Optimized');

    if (supportsIntersectionObserver()) {
      initModernLazyLoading();
    } else {
      initFallbackLazyLoading();
    }

    addRequiredStyles();
    logInitialStats();
  }

  function initModernLazyLoading() {
    createImageObserver();
    setupProgressiveLoading();
    processInitialImages();
  }

  function createImageObserver() {
    imageObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          loadImage(entry.target);
          imageObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: config.rootMargin,
      threshold: config.threshold,
    });
  }

  function setupProgressiveLoading() {
    var entries = document.querySelectorAll(config.timelineSelector);
    totalEntries = entries.length;

    if (totalEntries <= config.initialVisible) {
      console.log('All entries visible, no progressive loading needed');
      return;
    }

    hideExcessEntries(entries);
    setupScrollHandler();

    console.log(
      'Progressive loading setup: ' + config.initialVisible + ' visible, ' +
        (totalEntries - config.initialVisible) + ' hidden',
    );
  }

  function hideExcessEntries(entries) {
    currentlyVisible = 0;

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      // Skip elements marked with no-lazy-limit
      if (
        entry.classList.contains('no-lazy-limit') ||
        entry.closest('.no-lazy-limit')
      ) {
        entry.classList.add('lazy-visible');
        entry.style.display = '';
        continue;
      }

      if (i >= config.initialVisible) {
        entry.style.display = 'none';
        entry.classList.add('lazy-hidden');

        // Log details about the hidden image/element
        var img = entry.tagName === 'IMG' ? entry : entry.querySelector('img');
        console.debug('[LazyLoad] Hidden excess entry:', {
          index: i,
          element: entry,
          imgSrc: img ? (img.currentSrc || img.src) : 'No <img> found',
          alt: img ? img.alt : '',
          parentClasses: entry.parentElement
            ? entry.parentElement.className
            : '',
        });
      } else {
        entry.classList.add('lazy-visible');
        currentlyVisible++;
      }
    }
  }

  function processInitialImages() {
    var visibleEntries = document.querySelectorAll('.lazy-visible');
    var processedCount = 0;

    for (var i = 0; i < visibleEntries.length; i++) {
      var entry = visibleEntries[i];
      var images = entry.querySelectorAll('img');

      for (var j = 0; j < images.length; j++) {
        var img = images[j];
        if (shouldProcessImage(img)) {
          prepareImageForLazyLoading(img);
          imageObserver.observe(img);
          processedCount++;
        }
      }
    }

    console.log('Processed ' + processedCount + ' initial images');
  }

  function shouldProcessImage(img) {
    return img.src &&
      img.src.indexOf('data:image') === -1 &&
      !img.classList.contains('lazy-image') &&
      !loadedImages.has(img.src) &&
      img.offsetParent !== null;
  }

  function storeOriginalStyles(img) {
    var key = img.src || img.dataset.src;
    if (key && !originalStyles.has(key)) {
      // Only store explicitly set inline styles, NOT computed styles
      // This prevents restoring large computed dimensions
      originalStyles.set(key, {
        width: img.style.width || '',
        height: img.style.height || '',
        maxWidth: img.style.maxWidth || '',
        objectFit: img.style.objectFit || '',
        classes: img.className,
      });
    }
  }

  function prepareImageForLazyLoading(img) {
    // Store original styles before modifying
    storeOriginalStyles(img);

    if (!img.dataset.src) {
      img.dataset.src = img.src;
    }

    img.src = generatePlaceholder();
    img.classList.add('lazy-image');

    // Use CSS classes instead of inline styles for better responsive behavior
    // Only set minimal inline styles that won't conflict with responsive CSS
    img.style.transition = 'filter ' + config.fadeInDuration +
      'ms ease, opacity ' + config.fadeInDuration + 'ms ease';
  }

  function loadImage(img) {
    var src = img.dataset.src;
    if (!src || loadedImages.has(src)) {
      return;
    }

    var loader = new Image();

    loader.onload = function () {
      img.src = src;
      img.classList.remove('lazy-image');
      img.classList.add('lazy-loaded');

      // Restore original responsive styles
      restoreOriginalStyles(img, src);

      loadedImages.add(src);
    };

    loader.onerror = function () {
      img.classList.add('lazy-error');
      // Still restore styles even on error
      restoreOriginalStyles(img, src);
    };

    loader.src = src;
  }

  function restoreOriginalStyles(img, src) {
    var original = originalStyles.get(src);

    // Clear any inline styles that might have been set
    img.style.width = '';
    img.style.height = '';

    if (original) {
      // Only restore explicitly set inline styles
      if (original.width) {
        img.style.width = original.width;
      }
      if (original.height) {
        img.style.height = original.height;
      }
      if (original.maxWidth) {
        img.style.maxWidth = original.maxWidth;
      } else {
        // Ensure max-width for responsive behavior
        img.style.maxWidth = '100%';
      }
      if (original.objectFit) {
        img.style.objectFit = original.objectFit;
      }
    } else {
      // No original styles stored, use responsive defaults
      img.style.maxWidth = '100%';
    }
  }

  function setupScrollHandler() {
    function handleScroll() {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(function () {
        checkIfShouldLoadMore();
      }, config.debounceDelay);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', function () {
      setTimeout(checkIfShouldLoadMore, 100);
    });
  }

  function checkIfShouldLoadMore() {
    if (isLoading || currentlyVisible >= totalEntries) {
      return;
    }

    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var scrollHeight = document.documentElement.scrollHeight;
    var clientHeight = document.documentElement.clientHeight;

    // Calculate scroll percentage
    var scrollPercentage = scrollTop / (scrollHeight - clientHeight);

    // Check distance from last visible entry (more aggressive on mobile)
    var lastVisibleEntry = document.querySelector('.lazy-visible:last-of-type');
    var shouldLoadByPosition = false;

    if (lastVisibleEntry) {
      var rect = lastVisibleEntry.getBoundingClientRect();
      var distanceFromBottom = rect.bottom - clientHeight;
      var threshold = isMobileDevice() ? 600 : 800;
      shouldLoadByPosition = distanceFromBottom < threshold;
    }

    var shouldLoad = scrollPercentage > config.scrollThreshold ||
      shouldLoadByPosition;

    if (shouldLoad) {
      loadNextBatch();
    }
  }

  function loadNextBatch() {
    if (isLoading) {
      return;
    }

    isLoading = true;

    var hiddenEntries = document.querySelectorAll('.lazy-hidden');
    var nextBatch = Array.prototype.slice.call(
      hiddenEntries,
      0,
      config.batchSize,
    );

    if (nextBatch.length === 0) {
      console.log('All timeline entries loaded');
      isLoading = false;
      return;
    }

    revealEntries(nextBatch);

    setTimeout(function () {
      isLoading = false;
      setTimeout(checkIfShouldLoadMore, 100);
    }, nextBatch.length * 100 + 300);
  }

  function revealEntries(entries) {
    entries.forEach(function (entry, index) {
      setTimeout(function () {
        showEntryWithAnimation(entry);
        processImagesInEntry(entry);
        currentlyVisible++;
      }, index * 100);
    });

    console.log(
      'Loaded ' + entries.length + ' more entries (' + currentlyVisible + '/' +
        totalEntries + ')',
    );
  }

  function showEntryWithAnimation(entry) {
    entry.style.display = 'block';
    entry.style.opacity = '0';
    entry.style.transform = 'translateY(30px)';
    entry.style.transition = 'opacity 0.5s ease, transform 0.5s ease';

    requestAnimationFrame(function () {
      entry.style.opacity = '1';
      entry.style.transform = 'translateY(0)';
    });

    entry.classList.remove('lazy-hidden');
    entry.classList.add('lazy-visible');
  }

  function processImagesInEntry(entry) {
    var images = entry.querySelectorAll('img');
    var newImageCount = 0;

    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      if (shouldProcessImage(img)) {
        prepareImageForLazyLoading(img);
        if (imageObserver) {
          imageObserver.observe(img);
        }
        newImageCount++;
      }
    }

    if (newImageCount > 0) {
      console.log('Added ' + newImageCount + ' new images to lazy loading');
    }
  }

  function generatePlaceholder() {
    var svg = [
      '<svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">',
      '<rect width="100%" height="100%" fill="#f8f9fa"/>',
      '<rect x="20" y="20" width="260" height="160" fill="none" stroke="#dee2e6" stroke-width="1" stroke-dasharray="4,4"/>',
      '<circle cx="150" cy="100" r="15" fill="#6c757d" opacity="0.5"/>',
      '<text x="150" y="130" font-family="Arial" font-size="12" fill="#6c757d" text-anchor="middle">Loading...</text>',
      '</svg>',
    ].join('');

    return 'data:image/svg+xml;base64,' + btoa(svg);
  }

  function addRequiredStyles() {
    if (document.getElementById('lazy-loading-styles')) {
      return;
    }

    var style = document.createElement('style');
    style.id = 'lazy-loading-styles';
    style.textContent = [
      // Base lazy image styles - preserve responsive behavior
      '.lazy-image {',
      'background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);',
      'background-size: 200% 100%;',
      'animation: lazy-shimmer 2s infinite;',
      'border: 1px solid #dee2e6;',
      'border-radius: 3px;',
      'filter: blur(2px);',
      'opacity: 0.8;',
      '}',

      // Preserve responsive classes
      '.lazy-image.img-responsive {',
      'width: 100% !important;',
      'height: auto !important;',
      'max-width: 100% !important;',
      '}',

      '@keyframes lazy-shimmer {',
      '0% { background-position: 200% 0; }',
      '100% { background-position: -200% 0; }',
      '}',

      '.lazy-loaded {',
      'animation: lazy-fade-in 0.4s ease forwards;',
      'border: none !important;',
      'filter: none !important;',
      'opacity: 1 !important;',
      '}',

      '@keyframes lazy-fade-in {',
      'from { opacity: 0.8; transform: scale(0.99); }',
      'to { opacity: 1; transform: scale(1); }',
      '}',

      '.lazy-error {',
      'background: #f8d7da !important;',
      'border: 1px solid #f5c6cb !important;',
      'color: #721c24;',
      'display: flex;',
      'align-items: center;',
      'justify-content: center;',
      'min-height: 100px;',
      'font-size: 11px;',
      'border-radius: 3px;',
      'filter: none !important;',
      '}',

      '.lazy-error::before {',
      'content: "Image unavailable";',
      '}',

      '.lazy-hidden {',
      'display: none !important;',
      '}',

      '.lazy-visible {',
      //'display: block;',
      '}',

      // Mobile-specific responsive handling
      '@media (max-width: 768px) {',
      '.lazy-image {',
      'width: 100% !important;',
      'height: auto !important;',
      'max-width: 100% !important;',
      'display: block !important;',
      '}',

      '.lazy-loaded {',
      'width: 100% !important;',
      'height: auto !important;',
      'max-width: 100% !important;',
      '}',
      '}',

      // Tablet landscape
      '@media (min-width: 769px) and (max-width: 1024px) {',
      '.lazy-image, .lazy-loaded {',
      'max-width: 100%;',
      'height: auto;',
      '}',
      '}',

      '@media (prefers-reduced-motion: reduce) {',
      '.lazy-image, .lazy-loaded {',
      'animation: none !important;',
      'transition: opacity 0.2s ease !important;',
      '}',
      '}',

      '@media print {',
      '.lazy-hidden { display: block !important; }',
      '.lazy-image { ',
      'filter: none !important; ',
      'opacity: 1 !important; ',
      'background: none !important; ',
      'animation: none !important;',
      '}',
      '}',
    ].join('');

    document.head.appendChild(style);
  }

  function initFallbackLazyLoading() {
    console.log('Using fallback lazy loading');

    setupProgressiveLoading();
    processInitialImagesForFallback();
    setupFallbackScrollHandler();
  }

  function processInitialImagesForFallback() {
    var visibleEntries = document.querySelectorAll('.lazy-visible');

    for (var i = 0; i < visibleEntries.length; i++) {
      var entry = visibleEntries[i];
      var images = entry.querySelectorAll('img');

      for (var j = 0; j < images.length; j++) {
        var img = images[j];
        if (shouldProcessImage(img)) {
          prepareImageForLazyLoading(img);
        }
      }
    }
  }

  function setupFallbackScrollHandler() {
    function handleFallbackScroll() {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(function () {
        checkVisibleImages();
        checkIfShouldLoadMore();
      }, config.debounceDelay);
    }

    window.addEventListener('scroll', handleFallbackScroll);
    checkVisibleImages();
  }

  function checkVisibleImages() {
    var images = document.querySelectorAll('img[data-src]');

    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      if (isElementInViewport(img)) {
        loadImage(img);
      }
    }
  }

  function isElementInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight + 100 &&
      rect.bottom > -100 &&
      rect.left < window.innerWidth &&
      rect.right > 0;
  }

  function logInitialStats() {
    setTimeout(function () {
      var stats = getLoadingStats();
      console.log('Initial stats:', stats);
      console.log('Mobile device:', isMobileDevice());
    }, 500);
  }

  function getLoadingStats() {
    return {
      loadedImages: loadedImages.size,
      visibleEntries: currentlyVisible,
      hiddenEntries: totalEntries - currentlyVisible,
      totalEntries: totalEntries,
      totalImages: document.querySelectorAll(config.imageSelector).length,
      isMobile: isMobileDevice(),
    };
  }

  function forceLoadAll() {
    var hiddenEntries = document.querySelectorAll('.lazy-hidden');
    var lazyImages = document.querySelectorAll('img[data-src]');

    for (var i = 0; i < hiddenEntries.length; i++) {
      var entry = hiddenEntries[i];
      entry.style.display = 'block';
      entry.classList.remove('lazy-hidden');
      entry.classList.add('lazy-visible');
      currentlyVisible++;
    }

    for (var j = 0; j < lazyImages.length; j++) {
      loadImage(lazyImages[j]);
    }

    console.log('All content force loaded');
  }

  function initialize() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initLazyLoading);
    } else {
      initLazyLoading();
    }
  }

  // Public API
  window.timelineLazyLoading = {
    init: initialize,
    forceLoadAll: forceLoadAll,
    getStats: getLoadingStats,
    loadMore: function () {
      if (!isLoading) {
        loadNextBatch();
      }
    },
    version: '2.2',
  };

  // Auto-initialize
  initialize();
})();

console.log('Timeline Lazy Loading v2.2 - Mobile Optimized Ready');
